import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronDown, Copy, MessageSquare, Share2, Upload, X } from 'lucide-react';
import { MAIL_CLIENT_LABEL, canNativeShare, openDraft, phoneUrl, shareOrCopy } from '@/lib/composeDispatch';
import { MailClientButton, useMailClient } from '@/components/share/MailClientButton';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { ListRowButton } from '@/components/ui/list-row';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { VoiceTextarea } from '@/components/ui/voice-textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useBuyerInviteLink } from '@/hooks/useReferralProgram';
import { useBuyerRequests } from '@/hooks/useBuyerRequests';
import { useAuth } from '@/hooks/useAuth';
import { AIThinkingLabel } from '@/components/ai/AIThinkingLabel';
import { trackActivity } from '@/lib/activityTracking';
import { useFeatureRegistration, type FeatureMeta } from '@/contexts/FeatureRegistryContext';
import {
  SUPPLIER_INVITE_TEMPLATES, supplierInviteMessage, multiRequestInviteBody, parseSupplierList, batches,
  type SupplierInviteTone,
} from '@/lib/supplierInvite';

export const FEATURE_META: FeatureMeta = {
  id: 'invite-supplier-or-broker',
  label: 'Invite your suppliers',
  description: 'Paste your supplier list once (emails, phones, a spreadsheet column); Cannafax remembers it for your company, and every Buy Request can go to all of them from your own email or phone.',
  triggerType: 'dialog',
  personas: ['buyer'],
};

type SavedContact = {
  id: string; name: string | null; email: string | null; phone: string | null;
  last_invited_at: string | null; invite_count: number; already_connected: boolean; recently_invited: boolean;
};
const EMPTY_CONTACTS: SavedContact[] = [];

const daysAgo = (iso: string) => {
  const d = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));
  return d === 0 ? 'today' : d === 1 ? 'yesterday' : `${d} days ago`;
};

export default function InviteSupplierDialog({ open, onOpenChange, requestText }: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  /** The Buy Request the buyer came from; pre-ticked in the request list. */
  requestText?: string | null;
}) {
  useFeatureRegistration(FEATURE_META);
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: inviteUrl, isLoading: inviteLoading } = useBuyerInviteLink();
  // Branded short link, same shortener as listing and menu shares. Cached for
  // the session so reopening paints instantly; a failed shortener falls back
  // to the long link so the sheet never stays stuck.
  const short = useQuery({
    queryKey: ['buyer-invite-short-url', inviteUrl],
    enabled: open && !!inviteUrl,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    queryFn: async (): Promise<string> => {
      try {
        const { data } = await supabase.functions.invoke('shorten-url', { body: { url: inviteUrl } });
        return (data as { shortUrl?: string } | null)?.shortUrl ?? inviteUrl!;
      } catch { return inviteUrl!; }
    },
  });
  // Like Share listings: the sheet reveals once, fully formed, after the link is ready.
  const linkPending = inviteLoading || (!!inviteUrl && short.isPending);
  const url = short.data ?? inviteUrl ?? 'https://cannafax.ai';
  const shownUrl = (u: string) => u.replace(/^https?:\/\//, '');
  const { requests } = useBuyerRequests();
  const openRequests = useMemo(() => (requests as { id: string; raw_text: string; status: string }[]).filter((r) => r.status === 'active'), [requests]);

  const listKey = ['supplier-contacts', user?.id];
  const { data: saved = EMPTY_CONTACTS } = useQuery({
    queryKey: listKey, enabled: open && !!user,
    queryFn: async (): Promise<SavedContact[]> => {
      const { data, error } = await supabase.rpc('list_supplier_contacts' as never);
      if (error) throw error;
      return (data as unknown as SavedContact[]) ?? [];
    },
  });

  const [paste, setPaste] = useState('');
  // A plain invite always opens on a clean slate — nobody pre-ticked, saved
  // suppliers tucked behind "Choose from saved". Sending a Buy Request is the
  // one case where the saved list is the point, so eligible suppliers start ticked.
  const forRequest = !!requestText?.trim();
  const [selection, setSelected] = useState<Set<string> | null>(null);
  const selected = useMemo(() => selection ?? new Set(forRequest ? saved.filter((c) => !c.already_connected && !c.recently_invited).map((c) => c.id) : []), [selection, saved, forRequest]);
  const [requestSelection, setReqIds] = useState<Set<string> | null>(null);
  const reqIds = useMemo(() => {
    const hit = openRequests.find((r) => requestText && r.raw_text.trim() === requestText.trim());
    return requestSelection ?? new Set(hit ? [hit.id] : []);
  }, [requestSelection, openRequests, requestText]);
  const [tone, setTone] = useState<SupplierInviteTone>('default');
  const [body, setBody] = useState('');
  const [batch, setBatch] = useState(0);
  const [savedOpen, setSavedOpen] = useState(false);
  const [editing, setEditing] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);
  const [moreBelow, setMoreBelow] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Reset only on the open/close boundary, never in response to query fallbacks.
  useEffect(() => {
    if (!open) { setSelected(null); setReqIds(null); setBatch(0); setPhoneIdx(0); setPaste(''); setSavedOpen(false); setEditing(false); setLinkCopied(false); }
  }, [open]);


  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (el) setMoreBelow(el.scrollHeight - el.scrollTop - el.clientHeight > 8);
  }, []);

  const reqTexts = useMemo(() => {
    const picked = openRequests.filter((r) => reqIds.has(r.id)).map((r) => r.raw_text);
    if (picked.length === 0 && requestText?.trim() && !openRequests.some((r) => r.raw_text.trim() === requestText.trim())) return [requestText];
    return picked;
  }, [openRequests, reqIds, requestText]);
  useEffect(() => {
    if (!open) return;
    const base = (SUPPLIER_INVITE_TEMPLATES.find((t) => t.id === tone) ?? SUPPLIER_INVITE_TEMPLATES[0]).text;
    setBody(multiRequestInviteBody(reqTexts, base));
  }, [open, tone, reqTexts]);

  const preview = parseSupplierList(paste);
  // Pasted people are recipients right away — no separate "add" step. They are
  // saved to the company list at the moment the user sends.
  const savedKeys = new Set(saved.flatMap((c) => [c.email, c.phone].filter(Boolean) as string[]));
  const pasted = preview.contacts.filter((c) => !savedKeys.has((c.email ?? c.phone)!));
  const chosen = saved.filter((c) => selected.has(c.id));
  const emails = [...chosen.flatMap((c) => c.email ? [c.email] : []), ...pasted.flatMap((c) => c.email ? [c.email] : [])];
  const phones = [...chosen.flatMap((c) => !c.email && c.phone ? [c.phone] : []), ...pasted.flatMap((c) => !c.email && c.phone ? [c.phone] : [])];
  const single = chosen.length === 1 && pasted.length === 0 ? chosen[0] : null;
  const full = supplierInviteMessage(body, url, single?.name?.split(' ')[0] ?? undefined);
  const withRequest = reqTexts.length > 0;
  const subject = withRequest ? 'Do you have this? My buy request on Cannafax' : 'Share your inventory with me through Cannafax';
  const emailBatches = batches(emails);
  const current = emailBatches[Math.min(batch, emailBatches.length - 1)] ?? [];
  const client = useMailClient();
  const draft = current.length > 1 ? { bcc: current, subject, body: full } : { to: current, subject, body: full };
  const [phoneIdx, setPhoneIdx] = useState(0);

  /** Saves pasted people, then returns the ids of every contact matching `keys`. */
  const persistAndResolve = async (keys: string[]): Promise<string[]> => {
    let list = saved;
    if (pasted.length > 0) {
      const { error } = await supabase.rpc('save_supplier_contacts' as never, { p_contacts: pasted } as never);
      if (error) { toast.error('Could not save your suppliers', { description: 'Your message still opened — they just weren’t remembered.' }); return []; }
      list = await qc.fetchQuery({ queryKey: listKey, queryFn: async () => {
        const { data, error: e } = await supabase.rpc('list_supplier_contacts' as never);
        if (e) throw e;
        return (data as unknown as SavedContact[]) ?? [];
      } }).catch(() => saved);
      setPaste('');
      setSelected((s) => { const n = new Set(s ?? selected); list.forEach((c) => { if (keys.includes((c.email ?? c.phone)!)) n.add(c.id); }); return n; });
    }
    return list.filter((c) => keys.includes((c.email ?? c.phone)!)).map((c) => c.id);
  };

  const afterEmail = () => {
    const sent = current;
    void persistAndResolve(sent).then((ids) => markInvited(ids, 'email', sent.length));
    if (batch + 1 < emailBatches.length) setBatch(batch + 1);
  };
  const sendPhone = () => {
    const p = phones[Math.min(phoneIdx, phones.length - 1)];
    if (!p) return;
    const ph = phoneUrl(p, full);
    openDraft(ph.url, full);
    toast.info(`Opening ${ph.label}…`, { description: 'Your message is also copied, in case it doesn’t open.' });
    void persistAndResolve([p]).then((ids) => markInvited(ids, 'sms', 1));
    setPhoneIdx((i) => (i + 1 < phones.length ? i + 1 : i));
  };
  const phoneVia = phoneUrl('0', '').label;

  const markInvited = async (ids: string[], route: string, recipients: number) => {
    trackActivity('supplier_invite_sent', { route, with_request: withRequest, recipients, saved_contacts: saved.length, requests: reqTexts.length });
    if (ids.length === 0) return;
    await supabase.rpc('mark_supplier_contacts_invited' as never, { p_ids: ids } as never);
    qc.invalidateQueries({ queryKey: listKey });
  };


  const onFile = async (f: File) => {
    try {
      if (/\.xlsx$/i.test(f.name)) {
        const ExcelJS = (await import('exceljs')).default;
        const wb = new ExcelJS.Workbook();
        await wb.xlsx.load(await f.arrayBuffer());
        const lines: string[] = [];
        wb.worksheets[0]?.eachRow((row) => { lines.push((row.values as unknown[]).slice(1).map((v) => (v && typeof v === 'object' && 'text' in (v as object) ? (v as { text: string }).text : String(v ?? ''))).join(' ')); });
        setPaste((p) => [p, ...lines].filter(Boolean).join('\n'));
      } else { const t = await f.text(); setPaste((p) => [p, t].filter(Boolean).join("\n")); }
    } catch { toast.error('Could not read that file', { description: 'Try a .csv or .xlsx file, or paste the list.' }); }
  };

  const remove = async (id: string) => {
    await supabase.rpc('remove_supplier_contact' as never, { p_id: id } as never);
    qc.invalidateQueries({ queryKey: listKey });
  };
  const toggle = (id: string) => { setSelected((s) => { const n = new Set(s ?? selected); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const copy = async (text: string, title: string, after: () => void) => {
    try { await navigator.clipboard.writeText(text); toast.success(title); after(); }
    catch { toast.error('Could not copy', { description: 'Select the text and copy it manually.' }); }
  };
  const canShare = canNativeShare();

  const pickable = saved.filter((c) => !c.already_connected);
  const showSaved = pickable.length > 0 && (savedOpen || forRequest);
  useEffect(() => { if (open) requestAnimationFrame(checkScroll); });
  const toggleReq = (id: string) => setReqIds((s) => { const n = new Set(s ?? reqIds); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const noneChosen = chosen.length === 0 && pasted.length === 0;
  const primaryIsEmail = emails.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 gap-0 flex flex-col max-h-[min(760px,calc(100dvh-2rem))] overflow-hidden">
        <DialogHeader className="px-5 pt-5 pb-4 space-y-1 text-left">
          <DialogTitle>{withRequest ? 'Send your request to your suppliers' : 'Invite your suppliers'}</DialogTitle>
          <DialogDescription>
            Sent from your own email or phone. Nothing goes out until you press Send.
          </DialogDescription>
        </DialogHeader>

        {linkPending ? <div className="flex min-h-[18rem] flex-1 items-center justify-center px-5 py-10">
            <AIThinkingLabel size="lg" context="generic" title="Preparing your invite link…" />
          </div> : <>
        <div className="relative flex-1 min-h-0 flex flex-col"><div ref={scrollRef} onScroll={checkScroll} className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 pb-5 space-y-6">
          {/* 0 — Fastest path: the visible short link */}
          <section className="space-y-1.5">
            <h3 className="text-sm font-medium">Invite link</h3>
            <div className="flex gap-2">
              <Input readOnly value={shownUrl(url)} aria-label="Invite link" onFocus={(e) => e.currentTarget.select()} className="num bg-muted text-base md:text-sm" />
              <Button variant="outline" className="shrink-0" onClick={() => copy(url, 'Invite link copied', () => { setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2000); markInvited([], 'copy', 0); })}>
                {linkCopied ? <Check className="mr-1.5 h-3.5 w-3.5" /> : <Copy className="mr-1.5 h-3.5 w-3.5" />}{linkCopied ? 'Copied' : 'Copy'}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">Text it to any supplier — their menu lands here when they join.</p>
          </section>

          {/* 1 — Who: always starts empty */}
          <section className="space-y-2">
            <div className="flex items-baseline justify-between">
              <h3 className="text-sm font-medium">To{(selected.size + pasted.length) > 0 && <span className="num ml-1.5 font-normal text-muted-foreground">{selected.size + pasted.length}</span>}</h3>
              {pickable.length > 0 && !forRequest && (
                <Button size="sm" variant="quiet" onClick={() => setSavedOpen((o) => !o)}>
                  {savedOpen ? 'Hide saved' : <>Choose from saved <span className="num ml-1">{pickable.length}</span></>}
                </Button>
              )}
            </div>
            <Textarea data-cue="supplier-invite-to" data-cue-next="supplier-invite-send" data-cue-return="invite-supplier"
              value={paste} onChange={(e) => setPaste(e.target.value)} rows={2} className="text-base md:text-sm resize-none"
              placeholder="Paste emails or phone numbers"
              onDrop={(e) => { const f = e.dataTransfer.files?.[0]; if (f) { e.preventDefault(); onFile(f); } }} />
            {preview.invalid.length > 0 && <p className="text-xs text-destructive">Check: {preview.invalid.join(', ')}</p>}
            <div className="flex items-center justify-between gap-2">
              <Button asChild size="sm" variant="quiet">
                <label className="cursor-pointer"><Upload className="mr-1.5 h-3.5 w-3.5" />Upload a sheet
                  <input type="file" accept=".csv,.txt,.xlsx" className="sr-only" onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }} />
                </label>
              </Button>
              {pasted.length > 0 && <span className="text-xs text-muted-foreground"><span className="num">{pasted.length}</span> ready · remembered when you send</span>}
            </div>
            {showSaved && (
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">Saved suppliers</p>
                  <Button size="sm" variant="quiet" onClick={() => setSelected(selected.size > 0 ? new Set() : new Set(pickable.filter((c) => !c.recently_invited).map((c) => c.id)))}>
                    {selected.size > 0 ? 'Clear' : 'Select all'}
                  </Button>
                </div>
                <ul className="max-h-48 overflow-y-auto overscroll-contain rounded-lg border divide-y">
                  {saved.map((c) => {
                    const note = c.already_connected ? 'Already shares a menu with you' : c.last_invited_at ? `Invited ${daysAgo(c.last_invited_at)}` : null;
                    return (
                      <li key={c.id} className="group flex items-center gap-3 px-3 py-2">
                        <Checkbox checked={selected.has(c.id)} disabled={c.already_connected} onCheckedChange={() => toggle(c.id)} aria-label={`Select ${c.name ?? c.email ?? c.phone}`} />
                        <ListRowButton disabled={c.already_connected} onClick={() => toggle(c.id)} className="min-h-0 min-w-0 flex-1 flex-col items-start gap-0 rounded-md px-1 py-0.5">
                          <p className="truncate text-sm">{c.name ?? c.email ?? c.phone}</p>
                          <p className="truncate text-xs text-muted-foreground">{[c.name ? (c.email ?? c.phone) : null, note].filter(Boolean).join(' · ')}</p>
                        </ListRowButton>
                        <Button size="icon" variant="quiet" className="h-7 w-7 opacity-0 group-hover:opacity-100 focus-visible:opacity-100" aria-label="Remove from my suppliers" onClick={() => remove(c.id)}><X className="h-3.5 w-3.5" /></Button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </section>

          {/* 2 — What */}
          {openRequests.length > 0 && (
            <section className="space-y-2">
              <h3 className="text-sm font-medium">Include your requests</h3>
              <div className="flex flex-wrap gap-2">
                {openRequests.map((r) => {
                  const on = reqIds.has(r.id);
                  return (
                    <Button key={r.id} type="button" variant="chip" size="chip" aria-pressed={on} onClick={() => toggleReq(r.id)}
                      className="max-w-full justify-start gap-1">
                      {on && <Check />}<span className="truncate">{r.raw_text}</span>
                    </Button>
                  );
                })}
              </div>
            </section>
          )}

          {/* 3 — Message */}
          <section className="space-y-2">
            <div className="flex items-baseline justify-between">
              <h3 className="text-sm font-medium">Message</h3>
              <Button size="sm" variant="quiet" onClick={() => setEditing((e) => !e)}>{editing ? 'Done' : 'Edit'}</Button>
            </div>
            {editing ? (
              <div className="space-y-2">
                <div role="radiogroup" aria-label="Tone" className="inline-flex rounded-md border p-0.5">
                  {SUPPLIER_INVITE_TEMPLATES.map((t) => (
                    <Button key={t.id} type="button" variant="quiet" role="radio" aria-checked={t.id === tone} onClick={() => setTone(t.id)}
                      className={`h-7 rounded px-2.5 text-xs ${t.id === tone ? 'bg-muted text-foreground' : ''}`}>{t.label}</Button>
                  ))}
                </div>
                <VoiceTextarea value={body} onValueChange={setBody} onChange={(e) => setBody(e.target.value)} rows={6} />
              </div>
            ) : (
              <Button type="button" variant="outline" onClick={() => setEditing(true)} className="h-auto w-full justify-start whitespace-pre-line break-words px-3 py-2.5 text-left text-sm font-normal text-foreground/80" aria-label="Edit message">
                {full}
              </Button>
            )}
          </section>
        </div>
        {moreBelow && (
          <Button variant="quiet" size="content" type="button" aria-label="More below" onClick={() => scrollRef.current?.scrollBy({ top: 200, behavior: 'smooth' })}
            className="font-normal absolute inset-x-0 bottom-0 flex h-10 items-end justify-center bg-gradient-to-t from-background to-transparent pb-1 text-muted-foreground">
            <ChevronDown className="h-4 w-4" />
          </Button>
        )}
        </div>

        <div className="px-5 py-4 border-t bg-background space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-1">
              <Button size="sm" variant="quiet" onClick={() => copy(full, 'Message copied', () => markInvited([], 'copy', 0))}><Copy className="mr-1.5 h-3.5 w-3.5" />Copy message</Button>
              {canShare && <Button size="icon" variant="quiet" className="h-8 w-8" aria-label="Share" onClick={async () => { markInvited([], 'native', 0); const o = await shareOrCopy(full); if (o === 'copied') toast.success('Message copied'); else if (o === 'failed') toast.error('Could not open the share sheet', { description: 'Copy the message and paste it anywhere.' }); }}><Share2 className="h-3.5 w-3.5" /></Button>}
            </div>
            <div className="flex items-center gap-2">
              {primaryIsEmail && phones.length > 0 && (
                <Button size="sm" variant="quiet" onClick={sendPhone}><MessageSquare className="mr-1.5 h-3.5 w-3.5" />
                  {phones.length > 1 ? `${phoneVia} ${Math.min(phoneIdx, phones.length - 1) + 1} of ${phones.length}` : `${phoneVia} 1 phone`}
                </Button>
              )}
              {primaryIsEmail ? (
                <MailClientButton
                  data-cue="supplier-invite-send"
                  draft={draft}
                  onSent={afterEmail}
                  label={(c) => emailBatches.length > 1 ? `Email part ${Math.min(batch, emailBatches.length - 1) + 1} of ${emailBatches.length}` : emails.length > 1 ? `Email ${emails.length} in ${MAIL_CLIENT_LABEL[c]}` : `Email in ${MAIL_CLIENT_LABEL[c]}`}
                />
              ) : phones.length > 0 ? (
                <Button data-cue="supplier-invite-send" onClick={sendPhone}><MessageSquare className="mr-1.5 h-4 w-4" />
                  {phones.length > 1 ? `${phoneVia} supplier ${Math.min(phoneIdx, phones.length - 1) + 1} of ${phones.length}` : `${phoneVia} supplier`}
                </Button>
              ) : (
                <Button disabled data-cue="supplier-invite-send">Add a supplier</Button>
              )}
            </div>
          </div>
          {!noneChosen && (
            <p className="text-xs text-muted-foreground">
              {primaryIsEmail ? `Opens a ready draft in ${MAIL_CLIENT_LABEL[client]} from your own address — you press Send.` : `Opens ${phoneVia} with your message ready — you press Send.`}
              {emails.length > 1 ? ' Everyone is blind-copied.' : ''}
              {emailBatches.length > 1 ? ` Long lists go in ${emailBatches.length} emails.` : ''}
            </p>
          )}
        </div>
        </>}
      </DialogContent>
    </Dialog>
  );
}
